/**
 * Helpers for turning a formula copied from a mathlive `<math-field>` into editor math.
 *
 * On copy, mathlive writes the raw formula to the `application/x-latex` clipboard entry AND a
 * human-readable `$$ … $$` form (note the surrounding spaces) to `text/plain`. The
 * tiptap-math-extension's paste rule only recognises a *tightly* delimited `$$…$$` — its regex has
 * a `(?!\s)` guard right after the opening `$$` — so mathlive's spaced `$$ … $$` matches nothing
 * and pastes as raw LaTeX text instead of a rendered formula (bug 529112).
 */

// The clipboard MIME type mathlive writes the raw (un-delimited) formula to on copy.
export const MATHLIVE_LATEX_CLIPBOARD_TYPE = 'application/x-latex';

/**
 * Pull a single LaTeX formula out of a clipboard payload that originated from a mathlive field, or
 * `null` if the payload isn't a single whole formula. Prefer the unambiguous `application/x-latex`
 * entry; fall back to stripping the delimiters off a `text/plain` payload only when the WHOLE
 * payload is one delimited formula (mixed text+math pastes are left to the extension's paste rules).
 */
export function extractPastedLatex(clipboardData: DataTransfer | null): string | null {
  if (!clipboardData) return null;

  const xLatex = clipboardData.getData(MATHLIVE_LATEX_CLIPBOARD_TYPE).trim();
  if (xLatex) return xLatex;

  const plain = clipboardData.getData('text/plain').trim();
  const match = /^\$\$([\s\S]+)\$\$$/.exec(plain) ?? /^\$([\s\S]+)\$$/.exec(plain);
  if (!match) return null;

  const inner = match[1].trim();
  // Bail on an interior unescaped `$` — that's more than one run, not a single formula. Matched with
  // a leading "start-or-non-backslash" group rather than a lookbehind (`(?<!\\)`), which the project's
  // es2015 target / older Safari cannot parse and would throw a SyntaxError on at module load.
  if (/(^|[^\\])\$/.test(inner)) return null;
  return inner || null;
}
